// CNC 파트너 — 케이스 보드 (2026-10-23, UI 구조 단계)
// 홈(오늘 보드)과 케이스(전체 이력) 두 화면이 같이 쓴다. 기본 정렬 = 납기 빠른 순.
// 모바일: 케이스ID·상태·납기·다운로드만. 출고 처리는 모바일에서도 된다.
import React, { useMemo, useState } from 'react';
import { Download, Search, AlertTriangle, Loader, MessageSquare, RotateCcw } from 'lucide-react';
import { STATUS, STATUS_ORDER, JIG, KIND_ORDER, KindChip, StatusBadge, ddayText, isDueSoon, isToday, isUnshipped, fmtMd, patientNameOf, ui } from './partnerUi';
import { CNC_NAME_ON } from './partnerSample';
import { MF_KIND_LABEL, detailSummary, teethLabel } from '../../services/outsourceMfApi';
import PartnerCasePanel from './PartnerCasePanel';

// 요약 카드에서 넘어오는 빠른 필터
export const QUICK_FILTERS = {
    all: { label: '전체', test: () => true },
    newToday: { label: '신규 접수', test: (c) => isToday(c.receivedAt) },
    waiting: { label: '밀링 대기', test: (c) => c.status === 'received' && !c.downloaded },
    dueSoon: { label: '납기 임박', test: isDueSoon },
    jigFail: { label: '지그 실패', test: (c) => c.jig === 'failed' },
    unshipped: { label: '출고 미확인', test: isUnshipped },
};

// 케이스 이름 = 환자 이름(크게) + 케이스번호(작게). CNC 실명 스위치 전에는 「케이스 번호로 확인」
function CaseName({ c }) {
    const name = patientNameOf(c, CNC_NAME_ON);
    return (
        <div style={{ minWidth: 0 }}>
            {name
                ? <div style={styles.patient}>{name}</div>
                : <div style={styles.patientHidden} title="CNC 업체는 약관 개정·법률 확인 후 환자 이름이 보입니다">{'케이스 번호로 확인'}</div>}
            <div style={styles.caseNoSmall}>{c.caseNo}</div>
        </div>
    );
}

function PartnerCaseBoard({ cases, setCases, quick = 'all', onQuickChange, labs, showPeriod = false, hideShipped = false, mobile }) {
    const [search, setSearch] = useState('');
    const [statusF, setStatusF] = useState('');
    const [labF, setLabF] = useState('');
    const [kindF, setKindF] = useState('');
    const [periodF, setPeriodF] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [openId, setOpenId] = useState(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const since = periodF ? Date.now() - Number(periodF) * 24 * 60 * 60 * 1000 : 0;
        return cases
            .filter((c) => (QUICK_FILTERS[quick] || QUICK_FILTERS.all).test(c))
            .filter((c) => !hideShipped || quick !== 'all' || c.status !== 'shipped')
            .filter((c) => !statusF || c.status === statusF)
            .filter((c) => !labF || c.labId === labF)
            .filter((c) => !kindF || c.kind === kindF)
            .filter((c) => !since || c.receivedAt.getTime() >= since)
            .filter((c) => !q || c.caseNo.toLowerCase().includes(q) || c.labName.toLowerCase().includes(q) || (patientNameOf(c, CNC_NAME_ON) || '').toLowerCase().includes(q))
            .sort((a, b) => a.dueAt - b.dueAt);
    }, [cases, quick, hideShipped, statusF, labF, kindF, periodF, search]);

    const setStatus = (ids, status) => {
        const set = new Set(Array.isArray(ids) ? ids : [ids]);
        setCases((prev) => prev.map((c) => (set.has(c.id)
            ? { ...c, status, downloaded: c.downloaded || status !== 'received', shippedAt: status === 'shipped' ? (c.shippedAt || new Date()) : null }
            : c)));
    };

    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
    const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

    const fakeDownload = (what) => alert(`미리보기: ${what} 다운로드는 서버 연결 후 동작합니다.`);
    const openRow = cases.find((c) => c.id === openId) || null;

    return (
        <div style={styles.wrap}>
            {/* 툴바 */}
            <div style={ui.toolBar}>
                <div style={styles.searchBox}>
                    <Search size={14} color="#94a3b8" style={styles.searchIcon} />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="환자 이름 · 기공소 · 케이스번호 검색"
                        style={{ ...ui.searchInput, paddingLeft: 30, maxWidth: 'none' }}
                    />
                </div>
                {!mobile && (
                    <>
                        <select style={ui.select} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                            <option value="">{'상태 전체'}</option>
                            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
                        </select>
                        <select style={ui.select} value={kindF} onChange={(e) => setKindF(e.target.value)}>
                            <option value="">{'종류 전체'}</option>
                            {KIND_ORDER.map((k) => <option key={k} value={k}>{MF_KIND_LABEL[k]}</option>)}
                        </select>
                        <select style={ui.select} value={labF} onChange={(e) => setLabF(e.target.value)}>
                            <option value="">{'기공소 전체'}</option>
                            {labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                        {showPeriod && (
                            <select style={ui.select} value={periodF} onChange={(e) => setPeriodF(e.target.value)}>
                                <option value="">{'기간 전체'}</option>
                                <option value="7">{'최근 7일'}</option>
                                <option value="30">{'최근 30일'}</option>
                                <option value="90">{'최근 90일'}</option>
                            </select>
                        )}
                    </>
                )}
                {quick !== 'all' && onQuickChange && (
                    <button type="button" style={styles.quickChip} onClick={() => onQuickChange('all')}>
                        {`${QUICK_FILTERS[quick].label} ✕`}
                    </button>
                )}
            </div>

            {/* 선택 일괄 작업 */}
            {!mobile && (
                <div style={styles.bulkBar}>
                    <span style={ui.muted}>{selected.size > 0 ? `${selected.size}건 선택` : '행을 선택하면 일괄 작업할 수 있습니다'}</span>
                    <button type="button" style={ui.ghostBtn} disabled={selected.size === 0} onClick={() => fakeDownload(`선택 ${selected.size}건 ZIP`)}>
                        <Download size={14} />{'선택 일괄 다운로드(ZIP)'}
                    </button>
                    <select
                        style={ui.select}
                        value=""
                        disabled={selected.size === 0}
                        onChange={(e) => { if (e.target.value) { setStatus([...selected], e.target.value); setSelected(new Set()); } }}
                    >
                        <option value="">{'선택 상태 일괄 변경'}</option>
                        {STATUS_ORDER.map((s) => <option key={s} value={s}>{`→ ${STATUS[s].label}`}</option>)}
                    </select>
                </div>
            )}

            {/* 보드 */}
            {rows.length === 0 ? (
                <div style={ui.empty}>{'해당하는 케이스가 없습니다.'}</div>
            ) : mobile ? (
                <div style={styles.mList}>
                    {rows.map((r) => {
                        const soon = isDueSoon(r);
                        return (
                            <div key={r.id} style={styles.mCard} onClick={() => setOpenId(r.id)}>
                                <div style={styles.mTop}>
                                    <CaseName c={r} />
                                    <span style={soon ? styles.red : styles.dim}>{`${fmtMd(r.dueAt)} ${ddayText(r.dueAt)}`}</span>
                                </div>
                                <div style={styles.mInfo}>
                                    <KindChip kind={r.kind} />
                                    <span style={styles.mText}>{`${teethLabel(r.teeth, r.bridge)} · ${detailSummary(r) || '—'}`}</span>
                                </div>
                                <div style={styles.mBottom} onClick={(e) => e.stopPropagation()}>
                                    <StatusBadge status={r.status} />
                                    <span style={{ flex: 1 }} />
                                    <button type="button" style={styles.iconBtn} onClick={() => fakeDownload('케이스 파일')}><Download size={14} /></button>
                                    {r.status !== 'shipped' && (
                                        <button type="button" style={styles.shipBtn} onClick={() => { if (window.confirm(`${patientNameOf(r, CNC_NAME_ON) || r.caseNo} 출고 처리할까요?\n출고된 건은 이번 달 명세서에 들어갑니다.`)) setStatus(r.id, 'shipped'); }}>{'출고'}</button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={styles.scrollTable}>
                    <table style={ui.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 36 }}><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="전체 선택" /></th>
                                <th style={styles.th}>{'상태'}</th>
                                <th style={styles.th}>{'환자 / 케이스'}</th>
                                <th style={styles.th}>{'기공소'}</th>
                                <th style={styles.th}>{'종류'}</th>
                                <th style={styles.th}>{'치아'}</th>
                                <th style={styles.th}>{'제작 내용'}</th>
                                <th style={styles.th}>{'파일'}</th>
                                <th style={styles.th}>{'접수일'}</th>
                                <th style={styles.th}>{'납기'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const soon = isDueSoon(r);
                                return (
                                    <tr key={r.id} style={{ ...styles.tr, ...(openId === r.id ? styles.trActive : {}) }} onClick={() => setOpenId(r.id)}>
                                        <td style={ui.td} onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} aria-label="선택" />
                                        </td>
                                        <td style={ui.td} onClick={(e) => e.stopPropagation()}>
                                            {/* 클릭으로 전환 — 기공소·치과 화면에 즉시 반영(서버 연결 후) */}
                                            <select
                                                value={r.status}
                                                onChange={(e) => setStatus(r.id, e.target.value)}
                                                style={{ ...styles.statusSelect, color: STATUS[r.status].fg, backgroundColor: STATUS[r.status].bg }}
                                            >
                                                {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
                                            </select>
                                        </td>
                                        <td style={ui.td}><CaseName c={r} /></td>
                                        <td style={ui.td}>{r.labName}</td>
                                        <td style={ui.td}><KindChip kind={r.kind} /></td>
                                        <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>{teethLabel(r.teeth, r.bridge)}</td>
                                        <td style={{ ...ui.td, maxWidth: 260 }}>
                                            {/* 기공소가 외주 의뢰서에 적은 상세 — CNC: 시스템·연결·플랫폼·직경 / 그 외: 품목·재료 */}
                                            <div style={styles.detailMain}>{detailSummary(r) || '—'}</div>
                                            <div style={styles.detailSub}>
                                                {[r.kind === 'cnc' ? r.detail.material : '', r.detail.shade, r.detail.postProcess, r.detail.anodizing ? `아노다이징 ${r.detail.anodizingColor || ''}`.trim() : '', r.detail.qty > 1 ? `${r.detail.qty}개` : ''].filter(Boolean).join(' · ')}
                                                {r.note && (
                                                    <span style={styles.noteMark} title={r.note}><MessageSquare size={11} />{'요청사항'}</span>
                                                )}
                                                {r.remake && (
                                                    <span style={styles.remakeMark} title={r.remake.reason}><RotateCcw size={11} />{r.remake.status === 'requested' ? '재제작 요청' : '재제작 처리됨'}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={ui.td} onClick={(e) => e.stopPropagation()}>
                                            <div style={styles.fileCell}>
                                                {/* 종류가 많아 파일 구분(디자인·지그 등) 없이 「파일」 하나로 케이스 파일 전부 받기 (대표 10-23) */}
                                                <button type="button" style={styles.fileBtn} title="케이스 파일 전체 받기" onClick={() => fakeDownload('케이스 파일')}>
                                                    <Download size={12} />{'파일'}
                                                </button>
                                                {r.jig === 'pending' ? (
                                                    <span style={{ ...styles.jigState, color: JIG.pending.fg }} title="지그 생성 중"><Loader size={12} />{'생성 중'}</span>
                                                ) : r.jig === 'failed' ? (
                                                    <span style={{ ...styles.jigState, color: JIG.failed.fg }} title="지그 생성 실패"><AlertTriangle size={12} />{'실패'}</span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>{fmtMd(r.receivedAt)}</td>
                                        <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>
                                            <span style={soon ? styles.red : undefined}>{fmtMd(r.dueAt)}</span>
                                            <span style={{ ...(soon ? styles.red : styles.dim), marginLeft: 6, fontSize: 12 }}>{ddayText(r.dueAt)}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <PartnerCasePanel row={openRow} onClose={() => setOpenId(null)} onStatus={setStatus} mobile={mobile} />
        </div>
    );
}

const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
    searchBox: { position: 'relative', flex: '1 1 200px', minWidth: 0, maxWidth: 300, display: 'flex' },
    searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' },
    quickChip: {
        padding: '6px 12px', border: '1px solid #c7d2fe', borderRadius: 999, backgroundColor: '#eef2ff',
        color: '#4338ca', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    bulkBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    tr: { cursor: 'pointer' },
    trActive: { backgroundColor: '#f5f3ff' },
    patient: { fontSize: 14, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' },
    patientHidden: { fontSize: 12, fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' },
    caseNoSmall: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', marginTop: 1 },
    caseNo: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' },
    statusSelect: {
        padding: '3px 8px', border: 'none', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', outline: 'none',
    },
    fileCell: { display: 'flex', alignItems: 'center', gap: 6 },
    fileBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px',
        border: '1px solid #99f6e4', borderRadius: 7, backgroundColor: '#f0fdfa',
        color: '#0f766e', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    detailMain: { fontSize: 13, color: '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    detailSub: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: '#64748b', marginTop: 2 },
    noteMark: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 999,
        backgroundColor: '#fff7ed', color: '#c2410c', fontSize: 11, fontWeight: 700,
    },
    remakeMark: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 999,
        backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700,
    },
    mInfo: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
    mText: { fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
    jigState: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    red: { color: '#dc2626', fontWeight: 700 },
    dim: { color: '#94a3b8' },
    // 모바일 카드
    // 목록은 화면 안에서 스크롤 — 머리줄 고정 (대표 10-23)
    scrollTable: { ...ui.tableWrap, overflow: 'auto', maxHeight: 'calc(100vh - 240px)', minHeight: 240 },
    th: { ...ui.th, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8fafc' },
    mList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
    mCard: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
    mTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 13 },
    mBottom: { display: 'flex', alignItems: 'center', gap: 8 },
    iconBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
        border: '1px solid #cbd5e1', borderRadius: 9, backgroundColor: '#fff', cursor: 'pointer', color: '#334155',
    },
    shipBtn: {
        height: 36, padding: '0 16px', border: 'none', borderRadius: 9, backgroundColor: '#047857',
        color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    },
};

export default PartnerCaseBoard;
